import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import type { ApiError } from '@/core/api';
import * as api from './api';

export const authKeys = {
  mfaStatus: ['auth', 'mfa', 'status'] as const,
};

/** Drives the SecurityPage UI — `enabled` + whether a pending setup exists. */
export function useMfaStatus() {
  return useQuery({
    queryKey: authKeys.mfaStatus,
    queryFn: () => api.getMfaStatus(),
  });
}

export function useStartMfaSetup() {
  return useMutation({
    mutationFn: () => api.startMfaSetup(),
    onError: (err: ApiError) => toast.error(err.message),
  });
}

export function useConfirmMfa() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (code: string) => api.confirmMfa(code),
    onSuccess: (data) => {
      qc.setQueryData(authKeys.mfaStatus, data);
      toast.success('Two-factor authentication enabled.');
    },
    // Errors are surfaced on the form, not toasted — they're field-level.
  });
}

export function useDisableMfa() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (code: string) => api.disableMfa(code),
    onSuccess: (data) => {
      qc.setQueryData(authKeys.mfaStatus, data);
      toast.success('Two-factor authentication disabled.');
    },
  });
}

export function useChangePassword() {
  return useMutation({
    mutationFn: (input: api.PasswordChangeInput) => api.changePassword(input),
    onSuccess: () => toast.success('Password changed.'),
    // Field-level errors (wrong current password, weak new one) render on the form.
  });
}
